import type { InputBaseComponentProps } from "@mui/material";
import { Box, useTheme } from "@mui/material";
import Link from "@tiptap/extension-link";
import UnderlineExtension from "@tiptap/extension-underline";
import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { forwardRef, useRef } from "react";
import { RichTextToolbar } from "./RichTextToolbar";

export interface RichTextInputProps
  extends Omit<InputBaseComponentProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  id?: string;
}

export const RichTextInput = forwardRef<HTMLDivElement, RichTextInputProps>(
  (
    { value, onChange, placeholder, maxLength, id, attributes, ...props },
    ref,
  ) => {
    const { colors, customSpacing, radius, typography } = useTheme();
    const lastValidContent = useRef<string>(value);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3, 4, 5, 6] },
        }),
        UnderlineExtension,
        Link.configure({
          openOnClick: true,
          HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
        }),
      ],
      content: value,
      onUpdate: ({ editor }: { editor: Editor }) => {
        const html = editor.getHTML();
        if (!maxLength || html.length <= maxLength) {
          lastValidContent.current = html;
          onChange(html);
        } else {
          editor.commands.setContent(lastValidContent.current, false);
        }
      },
      editorProps: {
        attributes: {
          placeholder: placeholder || "",
          ...attributes,
          style: `min-height: 200px; 
          color: ${colors.text.primary}; 
          background: ${colors.background.primary}; 
          padding: ${customSpacing.md}; 
          box-sizing: border-box;
          font-family: ${typography.fontFamily};
          ${attributes?.style || ""}`,
        },
      },
    });

    return (
      <Box
        ref={ref}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
        {...props}
      >
        <RichTextToolbar editor={editor} />
        <EditorContent
          editor={editor}
          id={id}
          style={{
            flex: 1,
            minHeight: 0,
            outline: "none",
            boxShadow: "none",
            border: `1px solid ${colors.border.primary}`,
            borderRadius: radius.md,
            transition: "border-color 0.2s, box-shadow 0.2s",
            overflow: "hidden",
          }}
        />
        <style>{`
          .ProseMirror:focus {
            outline: none !important;
            box-shadow: none !important;
          }
          .ProseMirror {
            height: 100%;
            min-height: 0;
            overflow: auto;
          }
          .ProseMirror p {
            margin: 0;
          }
          .ProseMirror h1,
          .ProseMirror h2,
          .ProseMirror h3,
          .ProseMirror h4,
          .ProseMirror h5,
          .ProseMirror h6 {
            margin: 0;
          }
          .ProseMirror a {
            cursor: pointer;
          }
        `}</style>
      </Box>
    );
  },
);
